import { useState, useMemo, useCallback } from "react";
import { GridSortModel, GridSortDirection } from "@mui/x-data-grid";

interface UseGridSortingOptions {
  /** The rows to sort */
  rows: any[];
  /** Fields that contain date values (Date objects or ISO strings) */
  dateFields?: string[];
  /** localStorage key for persisting the sort field */
  localStorageFieldKey?: string;
  /** localStorage key for persisting the sort direction */
  localStorageDirectionKey?: string;
}

interface UseGridSortingResult {
  /** Rows sorted according to the current sort model */
  sortedRows: any[];
  /** Current sort model (pass to DataGrid or EditableGrid) */
  sortModel: GridSortModel;
  /** Handler for sort model changes — persists to localStorage */
  onSortModelChange: (model: GridSortModel) => void;
  /** Current sort field (convenience accessor for EditableGrid's sortField prop) */
  sortField: string | undefined;
  /** Current sort direction (convenience accessor for EditableGrid's sortDirection prop) */
  sortDirection: GridSortDirection;
}

/**
 * Compare two non-null values.
 * Returns negative if a < b, positive if a > b, 0 if equal.
 */
function compareValues(a: any, b: any, isDateField: boolean): number {
  // Date comparison
  if (isDateField) {
    const timeA = a instanceof Date ? a.getTime() : new Date(a).getTime();
    const timeB = b instanceof Date ? b.getTime() : new Date(b).getTime();
    if (!isNaN(timeA) && !isNaN(timeB)) {
      return timeA - timeB;
    }
    // Fall through to string comparison if date parsing fails
  }

  // Boolean comparison (true before false in ascending order)
  if (typeof a === "boolean" && typeof b === "boolean") {
    if (a === b) return 0;
    return a ? -1 : 1;
  }

  // Number comparison
  if (typeof a === "number" && typeof b === "number") {
    return a - b;
  }

  // String comparison (default)
  return String(a).localeCompare(String(b));
}

/**
 * Custom hook for deterministic DataGrid sorting.
 *
 * Pre-sorts rows synchronously in `useMemo` so the DataGrid always receives
 * correctly ordered data. Pair with `sortingMode="server"` on the DataGrid
 * to prevent it from applying its own sort on top.
 *
 * This eliminates the timing / race-condition issues that arise from calling
 * `apiRef.current.applySorting()` with timeouts after row edits.
 *
 * @example
 * ```tsx
 * const { sortedRows, sortField, sortDirection, onSortModelChange } =
 *   useGridSorting({
 *     rows: gridItems,
 *     dateFields: ["dueDate", "createdDate"],
 *     localStorageFieldKey: "actor.sortField",
 *     localStorageDirectionKey: "actor.sortDirection",
 *   });
 *
 * <EditableGrid
 *   rows={sortedRows}
 *   sortField={sortField}
 *   sortDirection={sortDirection}
 *   updateSortModel={onSortModelChange}
 *   sortingMode="server"
 *   ...
 * />
 * ```
 */
export function useGridSorting({
  rows,
  dateFields = [],
  localStorageFieldKey,
  localStorageDirectionKey,
}: UseGridSortingOptions): UseGridSortingResult {
  // Stable set of date field names (only recomputed if the list changes)
  const dateFieldKey = dateFields.join(",");
  const dateFieldSet = useMemo(
    () => new Set(dateFields),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dateFieldKey],
  );

  // Initialise from localStorage if available
  const [sortModel, setSortModel] = useState<GridSortModel>(() => {
    if (localStorageFieldKey) {
      const field = localStorage.getItem(localStorageFieldKey);
      const direction = localStorageDirectionKey
        ? (localStorage.getItem(localStorageDirectionKey) as GridSortDirection)
        : ("asc" as GridSortDirection);
      if (field) {
        return [{ field, sort: direction || "asc" }];
      }
    }
    return [];
  });

  // Deterministic sort — runs synchronously whenever rows or sort model change
  const sortedRows = useMemo(() => {
    if (!sortModel.length || !rows.length) return rows;

    const { field, sort } = sortModel[0];
    const isDateField = dateFieldSet.has(field);
    const direction = sort === "desc" ? -1 : 1;

    return [...rows].sort((a, b) => {
      const valA = a[field];
      const valB = b[field];

      // Null / undefined always sort to the end regardless of direction
      if (valA == null && valB == null) return 0;
      if (valA == null) return 1;
      if (valB == null) return -1;

      return compareValues(valA, valB, isDateField) * direction;
    });
  }, [rows, sortModel, dateFieldSet]);

  // Persist sort preference and update state
  const onSortModelChange = useCallback(
    (model: GridSortModel) => {
      setSortModel(model);
      if (localStorageFieldKey) {
        if (model.length > 0) {
          localStorage.setItem(localStorageFieldKey, model[0].field);
          if (localStorageDirectionKey) {
            localStorage.setItem(
              localStorageDirectionKey,
              model[0].sort || "",
            );
          }
        } else {
          localStorage.removeItem(localStorageFieldKey);
          if (localStorageDirectionKey) {
            localStorage.removeItem(localStorageDirectionKey);
          }
        }
      }
    },
    [localStorageFieldKey, localStorageDirectionKey],
  );

  return {
    sortedRows,
    sortModel,
    onSortModelChange,
    sortField: sortModel[0]?.field,
    sortDirection: sortModel[0]?.sort,
  };
}
